import { create } from 'zustand'
import { toast } from 'react-hot-toast'
import workflowService from '@services/workflowService'

/**
 * Workflow Store
 *
 * Zustand store for managing workflow state
 */
export const useWorkflowStore = create((set, get) => ({
	// State
	workflows: [],
	selectedWorkflow: null,
	loading: false,
	error: null,
	stats: {
		total: 0,
		active: 0,
		disabled: 0,
		archived: 0,
	},
	filters: {
		search: '',
		status: 'all',
		sortBy: 'created_at',
		sortOrder: 'DESC',
	},
	companyUsers: [],
	roles: [],

	// Actions - Fetch
	fetchWorkflows: async (companyId) => {
		set({ loading: true, error: null })

		try {
			const { filters } = get()
			const response = await workflowService.getAll({
				company_id: companyId,
				status: filters.status !== 'all' ? filters.status : undefined,
				search: filters.search || undefined,
				sort_by: filters.sortBy,
				sort_order: filters.sortOrder,
			})

			if (response.success) {
				set({
					workflows: response.data || [],
					stats: response.stats || get().stats,
					loading: false,
				})
			} else {
				throw new Error(response.error || 'Failed to fetch workflows')
			}
		} catch (error) {
			console.error('Error fetching workflows:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to fetch workflows')
		}
	},

	fetchWorkflowById: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await workflowService.getById(id)

			if (response.success) {
				set({ selectedWorkflow: response.data, loading: false })
				return response.data
			} else {
				throw new Error(response.error || 'Failed to fetch workflow')
			}
		} catch (error) {
			console.error('Error fetching workflow:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to fetch workflow details')
			return null
		}
	},

	fetchCompanyUsers: async (companyId, role = null) => {
		try {
			const response = await workflowService.getCompanyUsers(companyId, role)

			if (response.success) {
				set({ companyUsers: response.data || [] })
				return response.data
			}
			return []
		} catch (error) {
			console.error('Error fetching company users:', error)
			return []
		}
	},

	// Actions - Create
	createWorkflow: async (workflowData) => {
		set({ loading: true, error: null })

		try {
			const response = await workflowService.create(workflowData)

			if (response.success) {
				const newWorkflow = response.data

				// Add to workflows list
				set((state) => ({
					workflows: [newWorkflow, ...state.workflows],
					loading: false,
				}))

				toast.success('Workflow created successfully')
				return newWorkflow
			} else {
				throw new Error(response.error || 'Failed to create workflow')
			}
		} catch (error) {
			console.error('Error creating workflow:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to create workflow')
			return null
		}
	},

	// Actions - Update
	updateWorkflow: async (id, workflowData) => {
		set({ loading: true, error: null })

		try {
			const response = await workflowService.update(id, workflowData)

			if (response.success) {
				const updatedWorkflow = response.data

				// Update in workflows list
				set((state) => ({
					workflows: state.workflows.map((w) => (w.id === id ? updatedWorkflow : w)),
					selectedWorkflow: state.selectedWorkflow?.id === id ? updatedWorkflow : state.selectedWorkflow,
					loading: false,
				}))

				toast.success('Workflow updated successfully')
				return updatedWorkflow
			} else {
				throw new Error(response.error || 'Failed to update workflow')
			}
		} catch (error) {
			console.error('Error updating workflow:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to update workflow')
			return null
		}
	},

	// Actions - Delete
	deleteWorkflow: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await workflowService.delete(id)

			if (response.success) {
				if (response.archived) {
					// Workflow was archived (has attached projects)
					set((state) => ({
						workflows: state.workflows.map((w) => (w.id === id ? { ...w, status: 'archived' } : w)),
						loading: false,
					}))
					toast.success('Workflow archived (has attached projects)')
				} else {
					// Workflow was deleted
					set((state) => ({
						workflows: state.workflows.filter((w) => w.id !== id),
						loading: false,
					}))
					toast.success('Workflow deleted successfully')
				}
				return true
			} else {
				throw new Error(response.error || 'Failed to delete workflow')
			}
		} catch (error) {
			console.error('Error deleting workflow:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to delete workflow')
			return false
		}
	},

	// Actions - Toggle Status
	toggleWorkflow: async (id) => {
		try {
			const response = await workflowService.toggle(id)

			if (response.success) {
				const updatedWorkflow = response.data

				set((state) => ({
					workflows: state.workflows.map((w) => (w.id === id ? { ...w, status: updatedWorkflow.status } : w)),
				}))

				toast.success(`Workflow ${updatedWorkflow.status === 'active' ? 'enabled' : 'disabled'}`)
				return true
			} else {
				throw new Error(response.error || 'Failed to toggle workflow')
			}
		} catch (error) {
			console.error('Error toggling workflow:', error)
			toast.error(error.message || 'Failed to toggle workflow status')
			return false
		}
	},

	// Filter actions
	setSearch: (search) => {
		set((state) => ({
			filters: { ...state.filters, search },
		}))
	},

	setStatusFilter: (status) => {
		set((state) => ({
			filters: { ...state.filters, status },
		}))
	},

	setSorting: (sortBy, sortOrder) => {
		set((state) => ({
			filters: { ...state.filters, sortBy, sortOrder },
		}))
	},

	resetFilters: () => {
		set({
			filters: {
				search: '',
				status: 'all',
				sortBy: 'created_at',
				sortOrder: 'DESC',
			},
		})
	},

	// Selection
	selectWorkflow: (workflow) => {
		set({ selectedWorkflow: workflow })
	},

	clearSelectedWorkflow: () => {
		set({ selectedWorkflow: null })
	},

	// Reset store
	reset: () => {
		set({
			workflows: [],
			selectedWorkflow: null,
			loading: false,
			error: null,
			stats: {
				total: 0,
				active: 0,
				disabled: 0,
				archived: 0,
			},
			filters: {
				search: '',
				status: 'all',
				sortBy: 'created_at',
				sortOrder: 'DESC',
			},
			companyUsers: [],
		})
	},
}))

export default useWorkflowStore
